import { Injectable, OnDestroy } from '@angular/core';
import get from 'lodash-es/get';
import { BehaviorSubject, combineLatest, filter, map, Observable, of, Subscription } from 'rxjs';
import { BasePermissionsFunctions } from '../abstract/base-permissions-functions';
import { permissions as permissionsJSON } from '../constants/permissions';
import { CurrentShijiUserInterface } from '../interfaces/current-shiji-user.interface';
import { CurrentSupplierUserInterface } from '../interfaces/current-supplier-user.interface';
import { FeatureGroup } from '../interfaces/feature-group.interface';
import { isPermissionJSON, PermissionJSON, PermissionObject } from '../interfaces/permissions.interface';
import { unsubscribeFromAll } from '../util/unsubscribeFromAll';
import { UserApiService } from './userApi.service';

@Injectable({
    providedIn: 'root',
})
export class PermissionsService extends BasePermissionsFunctions implements OnDestroy {
    private permissionsSubject: BehaviorSubject<any> = new BehaviorSubject(undefined);
    private subscriptions: { [key: string]: Subscription } = {};

    constructor(private userApiService: UserApiService) {
        super();
        this.subscriptions.permissionsJson = of(permissionsJSON).subscribe((value) => {
            this.permissionsSubject.next(value);
        });
    }

    private getPermissionLabels(): Observable<any> {
        return this.permissionsSubject.asObservable().pipe(filter((value) => Boolean(value)));
    }

    private getUserAccess(): Observable<CurrentSupplierUserInterface | CurrentShijiUserInterface> {
        return this.userApiService.getLoginInformationContinuously();
    }

    private getResourcesInformation(): Observable<{ [id: string]: FeatureGroup }> {
        return this.userApiService.getResourcesInformation().pipe(
            map((resources) => {
                if (!resources) {
                    return {};
                }

                const resourceMap: { [key: string]: FeatureGroup } = {};

                resources.forEach((resource) => {
                    resourceMap[resource.id!] = resource;
                });
                return resourceMap;
            }),
        );
    }

    public getPermissions(): Observable<[any, CurrentSupplierUserInterface | CurrentShijiUserInterface, { [id: string]: FeatureGroup }]> {
        return combineLatest([this.getPermissionLabels(), this.getUserAccess(), this.getResourcesInformation()]);
    }

    ngOnDestroy() {
        unsubscribeFromAll(this.subscriptions);
    }

    public userHasAccessToAll(permissionLabels: string[]): Observable<boolean[]> {
        const hasAccessObservables: Observable<boolean>[] = permissionLabels.map((permissionLabel) => this.userHasAccess(permissionLabel));

        return combineLatest(hasAccessObservables);
    }

    public userHasAccess(appPermission: string): Observable<boolean> {
        let hasAccess = false;

        if (!appPermission) {
            return of(hasAccess);
        }

        if (appPermission === 'appPermissionAlwaysShow') {
            hasAccess = true;
            return of(hasAccess);
        }

        if (this.subscriptions.permissions) {
            this.subscriptions.permissions.unsubscribe();
            delete this.subscriptions.permissions;
        }

        return this.getPermissions().pipe(
            map(([permissionLabelsObject, currentUser, resources]) => {
                const permissionKey = appPermission;
                let permissionObject: PermissionJSON | undefined = get(permissionLabelsObject, permissionKey);

                if (!permissionObject) {
                    const segments = permissionKey.split('.');
                    while (segments.length > 1 && !permissionObject) {
                        segments.pop();
                        const parentKey = segments.join('.');
                        const parent = get(permissionLabelsObject, parentKey) as PermissionObject | undefined;

                        // ⬇️ first check it’s *not* an array, then check your flag
                        if (parent && isPermissionJSON(parent) && parent.allowWildcardRoutes) {
                            permissionObject = parent;
                            break;
                        }
                    }
                }

                if (permissionLabelsObject && permissionObject) {
                    if (!this.isCurrentUserAShijiUser(currentUser)) {
                        currentUser = this.enrichUserResourcePermissionsObject(currentUser, resources);
                    }

                    return this.hasAccess(currentUser, permissionObject);
                }

                return false;
            }),
        );
    }
}
